/**
 * What GET /dashboard/clinic-summary answers with.
 *
 * The shape carries one decision worth knowing before reading any component: income is
 * present twice, not chosen by a request parameter. Switching the page between the two is
 * therefore a state change rather than a round trip, which is the whole reason the payload
 * looks like this.
 */

/**
 * The two honest ways to count what a package earned.
 *
 * BILLED counts the sale in full on the day it was made -- the cash view. EARNED counts
 * each session held at its share of the package price -- the delivery view. Neither is
 * more correct; a clinic admin needs both, which is why the API sends both.
 */
export type IncomeMode = 'BILLED' | 'EARNED';

export interface IClinicTotals {
    billed: number;
    /**
     * Can legitimately exceed `billed` for a period: sessions held now may belong to a
     * package sold earlier, and a cancelled sale keeps the sessions it already delivered.
     */
    earned: number;
    /**
     * What the clinic's therapists keep of `billed` between them -- the payroll cost of the
     * period. Equal to `billed` where no share has been set for anybody, which is what makes
     * the feature invisible to a clinic that does not use it.
     */
    billedShare: number;
    /** The same for `earned`. */
    earnedShare: number;
    /** Money taken for sessions not yet held, at the end of the period. */
    deferred: number;
    newPatients: number;
    existingPatients: number;
    activePatients: number;
    sessionsHeld: number;
    purchasesSold: number;
}

export interface ITherapistBreakdown {
    /** Null is the clinic's unassigned bucket -- purchases that name no therapist. */
    therapistId: number | null;
    /** Null alongside a null id. The label for that case is ours to supply; the API has no locale. */
    therapistName: string | null;
    billed: number;
    /**
     * What this therapist keeps of `billed`, at the rate in force on each sale's own date.
     * Equal to `billed` where the clinic has set them no share.
     *
     * **Not derivable from `billed` and `revenueShares`** -- a period whose rate changed
     * partway through is scaled row by row, and no single percentage reproduces it. Read this,
     * never a multiplication of your own.
     */
    billedShare: number;
    earned: number;
    /** The same for `earned`, at the rate in force on the day of each session held. */
    earnedShare: number;
    newPatients: number;
    existingPatients: number;
    activePatients: number;
    sessionsHeld: number;
    purchasesSold: number;
    /**
     * The rates in force at the **end** of the period — the default first, then each discipline
     * override — or empty where the clinic has set none and this therapist keeps the whole income.
     *
     * Labels rather than multipliers. Empty renders as an absence, never as a zero -- a zero
     * would say this person keeps nothing.
     */
    revenueShares: IRevenueShareLabel[];
}

/**
 * One rate a therapist is on, for saying so in words: their default (`catalogTypeId` null), or
 * one discipline's override, named in both languages as the clinic wrote it.
 */
export interface IRevenueShareLabel {
    catalogTypeId: number | null;
    nameRo: string | null;
    nameEn: string | null;
    percent: number;
}

export interface ITrendPoint {
    /** "2026-08". Months with no activity are present with zeros. */
    month: string;
    billed: number;
    earned: number;
    sessionsHeld: number;
    /** Unlike `activePatients`, these do sum to the year's figure. */
    newPatients: number;
    existingPatients: number;
    /**
     * Distinct patients seen that month. **Does not sum to the year's** -- somebody treated
     * from March to May is active in three months and is one person over the year. The
     * chart carries a caption saying so.
     */
    activePatients: number;
}

export interface ITherapistTrendPoint {
    /** The same string, at the same index, as the matching `trend` point. */
    month: string;
    billed: number;
    earned: number;
    sessionsHeld: number;
}

export interface ITherapistTrend {
    /** Null is the unassigned bucket -- purchases that name no therapist. */
    therapistId: number | null;
    therapistName: string | null;
    /** Exactly as many points as `trend`, same months, same order. Read by position. */
    points: ITherapistTrendPoint[];
}

export interface IClinicSummary {
    from: string;
    to: string;
    totals: IClinicTotals;
    /** Ordered by billed descending, the unassigned bucket last. */
    therapists: ITherapistBreakdown[];
    /** Always twelve, oldest first. Asked for a calendar year, that is January to December. */
    trend: ITrendPoint[];
    /**
     * The same twelve months per therapist -- one entry per series on the income chart.
     *
     * **Ordered by therapist id, unassigned last -- not by income**, unlike `therapists`.
     * The palette is keyed on this position, so the order has to be something that does
     * not move when the reader switches income mode or hides a colleague.
     */
    therapistTrend: ITherapistTrend[];
}

/**
 * The one place a mode turns into a number.
 *
 * Every widget the toggle drives reads through this rather than branching on the mode
 * itself, so adding a third way to count income later is one edit rather than five.
 */
export const incomeOf = (row: ITherapistBreakdown | IClinicTotals, mode: IncomeMode): number =>
    mode === 'BILLED' ? row.billed : row.earned;

/**
 * The same reading of the same row, in the money the therapist keeps rather than the money the
 * clinic billed.
 *
 * Beside `incomeOf` rather than folded into it because the two columns sit side by side and
 * must always answer the same question: an admin flipping the toggle must never end up with one
 * column counting sales and the other counting delivery.
 */
export const shareOf = (row: ITherapistBreakdown | IClinicTotals, mode: IncomeMode): number =>
    mode === 'BILLED' ? row.billedShare : row.earnedShare;

/** Serves both the clinic's months and a therapist's -- it only ever reads the two amounts. */
export const trendIncomeOf = (
    point: ITrendPoint | ITherapistTrendPoint,
    mode: IncomeMode
): number => (mode === 'BILLED' ? point.billed : point.earned);

/**
 * The identity a chart series is keyed and coloured by.
 *
 * Not the name: two therapists can share one, and a band axis or a series map keyed on the
 * name would silently merge them.
 */
export const therapistSeriesId = (series: ITherapistTrend): string =>
    series.therapistId === null ? 'unassigned' : String(series.therapistId);
